/**
 * TrialBanner Component
 * Shows remaining trial days with warning styling when <= 3 days left
 */
import { Link } from 'react-router-dom';

export interface TrialBannerProps {
  /** Number of days remaining in the trial */
  daysRemaining: number;
  /** Optional callback when upgrade button is clicked */
  onUpgradeClick?: () => void;
}

/**
 * Banner displayed at the top of the layout for trialing users.
 * Shows warning styling when 3 or fewer days remain.
 */
export default function TrialBanner({ daysRemaining, onUpgradeClick }: TrialBannerProps) {
  // Determine styling based on urgency
  const isUrgent = daysRemaining <= 3;

  const containerClasses = isUrgent
    ? 'bg-amber-50 border-b border-amber-200'
    : 'bg-blue-50 border-b border-blue-200';

  const iconClasses = isUrgent
    ? 'text-amber-400'
    : 'text-blue-400';

  const textClasses = isUrgent
    ? 'text-amber-800'
    : 'text-blue-800';

  const buttonClasses = isUrgent
    ? 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';

  // Format the message
  const getMessage = () => {
    if (daysRemaining === 0) {
      return 'Your trial ends today!';
    } else if (daysRemaining === 1) {
      return 'Your trial ends tomorrow!';
    } else {
      return `${daysRemaining} days left in your trial`;
    }
  };

  return (
    <div className={containerClasses} role="alert">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Icon and message */}
          <div className="flex items-center gap-3">
            {isUrgent ? (
              <WarningIcon className={`h-5 w-5 flex-shrink-0 ${iconClasses}`} />
            ) : (
              <InfoIcon className={`h-5 w-5 flex-shrink-0 ${iconClasses}`} />
            )}
            <p className={`text-sm font-medium ${textClasses}`}>
              {getMessage()}
              {!isUrgent && (
                <span className="hidden sm:inline">
                  {' '}Upgrade now to keep all your data and features.
                </span>
              )}
            </p>
          </div>

          {/* CTA button */}
          <Link
            to="/billing"
            onClick={onUpgradeClick}
            className={`
              inline-flex items-center px-4 py-2 rounded-md text-sm font-medium
              text-white shadow-sm transition-colors
              focus:outline-none focus:ring-2 focus:ring-offset-2
              ${buttonClasses}
            `}
          >
            {isUrgent ? 'Upgrade Now' : 'View Plans'}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}
